import axios from "axios";
import { baseURL } from "@/config";
import store from "./store";
import tokenManager from "@/tokenManager";
import i18n from "@/i18n";
import {
  forceLoginRedirect,
  markAuthenticationTermination,
  resetInactivityTimer,
} from "@/sessionManager";
const axiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    "Content-type": "application/json",
    "Accept": "application/json"
  }
});
axios.defaults.withCredentials = true;
axiosInstance.defaults.handleRouting = true;

/**
 * SECURITY: Request Interceptor
 * 
 * Handles two authentication modes:
 * 1. httpOnly Cookies (Sanctum stateful) - RECOMMENDED
 *    - Token is NOT stored in frontend
 *    - Automatically sent with requests via withCredentials
 *    - Safe from XSS attacks
 * 
 * 2. localStorage with TTL & Refresh Rotation - FALLBACK
 *    - Token stored locally with 1 hour TTL (configurable)
 *    - Automatically refreshed on use
 *    - Includes anomaly detection logging
 * 
 * Reference: Security Audit #15
 */
axiosInstance.interceptors.request.use(
  (config) => {
    const requiresAuth = config.requiresAuth !== undefined ? config.requiresAuth : axiosInstance.defaults.requiresAuth;

    if (requiresAuth !== false) {
      resetInactivityTimer({
        store,
        timeoutMs: 60 * 60 * 1000,
      });
    }
    
    if (!tokenManager.isUsingHttpOnlyCookies() && requiresAuth !== false) {
      // Fallback mode: localStorage with Bearer token
      const token = tokenManager.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        // Track token refresh for anomaly detection
        tokenManager.recordTokenRefresh();
      }
    }
    // httpOnly mode: no need to add Authorization header, cookies are sent automatically
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
    (response) => {
      const authorization = response?.data?.authorization;

      if (response?.config?.requiresAuth !== false) {
        resetInactivityTimer({
          timeoutMs: 60 * 1000,
        });
      }
 
      if (authorization && !response.config?.skipAuthorizationIngestion) {
        store.commit("replaceAuthorization", {
          payload: authorization,
          isSuperAdmin: response?.data?.user?.is_super_admin
            ?? response?.data?.data?.is_super_admin
            ?? store.getters.loggedUser?.is_super_admin === true,
        });
      }
      return response;
    },
    async (error) => {
      // Check if response exists before trying to access its properties
      if (!error.response) {
        // Network error or other client-side issue
        store.commit("setToast", {
          display: true,
          type: "error",
          message: i18n.global.t("messages.networkError"),
        });
        return Promise.reject(error);
      }


      const { status } = error.response;
      const message = error.response?.data?.message || "An error occurred";

      if (status === 401 && !error.config?.skipAuthTermination) {
        markAuthenticationTermination(error);

        // Log authentication failure for anomaly detection
        tokenManager.logAnomaly('Authentication failed (401)', {
          endpoint: error.config?.url,
          method: error.config?.method,
        });

        forceLoginRedirect({ store });

        // The page is being replaced. Keep the request chain pending so existing
        // component catch handlers cannot expose the expected 401 to the user.
        return new Promise(() => {});
      } else if (status === 403) {
        // if (axiosInstance.defaults.handleRouting) {
        //   router.push({
        //     name: "Dashboard",
        //   });
        // }
        store.commit("setToast", {
          display: true,
          type: "error",
          message: message,
        });

      } else if (status === 500) {
        store.commit("setToast", {
          display: true,
          type: "error",
          message: i18n.global.t("messages.somethingWentWrong"),
        });
      }

      return Promise.reject(error);
    }
);

export default ({ requiresAuth = true, handleRouting = true }) => {
  axiosInstance.defaults.requiresAuth = requiresAuth;
  axiosInstance.defaults.handleRouting = handleRouting;
  return axiosInstance;
};
